import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { buildPathFor } from "@/hooks/useLangPath";

const SITE_URL = (
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_SITE_URL) ||
  (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/$/, "");

const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_napoli-artigianalita/artifacts/ff6wp22h_logo%20CC.png";

const LOCALE_MAP = { it: "it_IT", en: "en_GB", ja: "ja_JP" };
const LANGS = ["it", "en", "ja"];
const SUPPORTED_PREFIXES = ["en", "ja"];

const SeoHead = ({ pageKey = "home", image, noIndex = false }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const lang = (i18n.resolvedLanguage || i18n.language || "it").slice(0, 2);
  const title = t(`seo.${pageKey}.title`);
  const description = t(`seo.${pageKey}.description`);
  const siteName = t("seo.siteName");
  const twitterHandle = t("seo.twitterHandle", "");
  const ogImage = image || DEFAULT_OG_IMAGE;

  // Compute the language-neutral base path (strip /en or /ja prefix if present)
  const rawPath = location.pathname || "/";
  let basePath = rawPath;
  const segments = rawPath.split("/").filter(Boolean);
  if (segments.length > 0 && SUPPORTED_PREFIXES.includes(segments[0])) {
    const remaining = segments.slice(1).join("/");
    basePath = remaining ? `/${remaining}` : "/";
  }

  const canonical = `${SITE_URL}${buildPathFor(lang, basePath === "/" ? "" : basePath)}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Caffè Cola 1960",
    alternateName: "Caffè Cola",
    url: `${SITE_URL}/`,
    logo: DEFAULT_OG_IMAGE,
    foundingDate: "1960",
    founder: { "@type": "Person", name: "Camillo Cola" },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via Marciotti 84/16",
      addressLocality: "San Giuseppe Vesuviano",
      addressRegion: "NA",
      postalCode: "80047",
      addressCountry: "IT",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: "+39 334 812 2013",
        email: "caffecola1960@libero.it",
        availableLanguage: ["Italian", "English", "Japanese"],
      },
    ],
    sameAs: [],
    description,
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates — prefix-based real URLs */}
      {LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE_URL}${buildPathFor(l, basePath === "/" ? "" : basePath)}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_URL}${buildPathFor("it", basePath === "/" ? "" : basePath)}`}
      />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={LOCALE_MAP[lang] || "it_IT"} />
      {LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_MAP[l]} />
      ))}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle ? <meta name="twitter:site" content={twitterHandle} /> : null}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Organization */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SeoHead;
